import { useState, useMemo } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton,
  Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme,
} from '@mui/material'
import { SearchableSelect } from '../../../components/SearchableSelect'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Asset, AssetCategory, Site } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useConfirm } from '../../../components/ConfirmDialog'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { usePermissions } from '../../../hooks/usePermissions'
import { AssetFormDialog } from './AssetFormDialog'
import { AttachmentSection } from '../../../components/AttachmentSection'
import { QrCodeDialog, qrPayload } from '../../../components/QrCodeDialog'
import { AssetTelemetryPanel } from './AssetTelemetryPanel'
import { AssetTrendsPanel } from './AssetTrendsPanel'
import { AssetWorkOrderHistory } from './AssetWorkOrderHistory'

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  active:             { label: 'Operational',        color: '#059669', bg: '#ECFDF5' },
  inactive:           { label: 'Inactive',           color: '#64748B', bg: '#F1F5F9' },
  under_maintenance:  { label: 'Under maintenance',  color: '#D97706', bg: '#FFFBEB' },
  decommissioned:     { label: 'Decommissioned',     color: '#DC2626', bg: '#FEF2F2' },
}

// Status is a manual field only — nothing (work orders included) flips
// it automatically, so the tooltip says so explicitly instead of
// implying some system logic decides it.
const STATUS_DESCRIPTIONS: Record<string, string> = {
  active:            'In normal service.',
  inactive:          'Not currently in use, but not retired — e.g. spare or seasonal equipment.',
  under_maintenance: 'Currently being serviced or repaired.',
  decommissioned:    'Permanently retired from service.',
}

const CRITICALITY_META: Record<string, { label: string; color: string; bg: string }> = {
  critical:  { label: 'Critical criticality',  color: '#DC2626', bg: '#FEF2F2' },
  important: { label: 'High criticality',      color: '#D97706', bg: '#FFFBEB' },
  normal:    { label: 'Normal',                color: '#059669', bg: '#ECFDF5' },
}

interface Fmt { formatDate: (d: Date | string, includeTime?: boolean) => string; formatCurrency: (v: number) => string; currencySymbol: string }

function daysBetween(a: string, b: string) {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000)
}

interface Props {
  asset: Asset
  categories: AssetCategory[]
  sites: Site[]
}

export function AssetDetail({ asset, categories, sites }: Props) {
  const qc = useQueryClient()
  const theme = useTheme()
  const navigate = useNavigate()
  const confirmDlg = useConfirm()
  const { formatDate, formatCurrency, prefs } = useOrgSettings()
  const { can } = usePermissions()
  const [tab, setTab] = useState<'insights' | 'telemetry' | 'trends' | 'details' | 'history'>('insights')
  const [editOpen, setEditOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)

  const statsQ = useQuery({
    queryKey: ['asset-stats', asset.id],
    queryFn: () => api.assets.getStats(asset.id),
    staleTime: 60_000,
  })
  const stats = statsQ.data

  // Determines whether the Telemetry tab shows at all — soft points are
  // a second, independent telemetry source alongside Arcos, so the tab
  // should appear even when there's no Arcos link.
  const softPointsQ = useQuery({
    queryKey: ['asset-soft-points', asset.id],
    queryFn: () => api.assets.listSoftPoints(asset.id),
    staleTime: 30_000,
  })
  const hasSoftPoints = (softPointsQ.data?.length ?? 0) > 0

  const st = (STATUS_META[asset.status] ?? { label: 'Active', color: '#059669', bg: '#ECFDF5' })
  // `||` (not `??`) so an empty string from the API also falls through —
  // alpha()/darken() throw MUI #9 ("Unsupported color") on '' or invalid hex.
  const catColor = asset.category_color || '#64748B'
  const meta = (asset.metadata ?? {}) as Record<string, any>
  const isDark = theme.palette.mode === 'dark'
  // Only rendered when a warranty date is actually on file — an asset
  // with no warranty_expiry has no warranty info, not an expired one.
  const warranty = asset.warranty_expiry
    ? (new Date(asset.warranty_expiry).getTime() >= Date.now()
        ? { label: 'In Warranty', color: '#059669', bg: '#ECFDF5' }
        : { label: 'Out of Warranty', color: '#DC2626', bg: '#FEF2F2' })
    : null

  const healthScore = useMemo(() => {
    let score = 100
    const now = new Date()
    if (asset.status === 'inactive') score -= 30
    if (asset.status === 'under_maintenance') score -= 15
    if (asset.status === 'decommissioned') score -= 50
    if (asset.warranty_expiry) {
      const wDays = daysBetween(now.toISOString(), asset.warranty_expiry)
      if (wDays < 0) score -= 15
      else if (wDays < 30) score -= 5
    }
    if (meta.next_maintenance_date) {
      const mDays = daysBetween(now.toISOString(), meta.next_maintenance_date)
      if (mDays < 0) score -= 25
      else if (mDays < 7) score -= 10
    }
    if (!meta.last_maintenance_date && meta.maintenance_interval) score -= 10
    return Math.max(0, Math.min(100, score))
  }, [asset, meta])

  const healthLabel = healthScore >= 80 ? 'Healthy' : healthScore >= 50 ? 'Fair' : 'Critical'
  const healthColor = healthScore >= 80 ? '#059669' : healthScore >= 50 ? '#D97706' : '#DC2626'

  const deleteM = useMutation({
    mutationFn: () => api.assets.remove(asset.id),
    onSuccess: () => { toast.success('Asset deleted'); qc.invalidateQueries({ queryKey: ['assets'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const locationStr = [asset.site_name, asset.location_name].filter(Boolean).join(' \u00b7 ')

  return (
    <motion.div key={asset.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>

      {/* Header */}
      <Box sx={{ px: 3.5, pt: 3, pb: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{
                width: 46, height: 46, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(catColor, 0.12), color: catColor, flexShrink: 0,
                border: '1.5px solid', borderColor: alpha(catColor, 0.25),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'scale(1.08)', boxShadow: `0 0 0 4px ${alpha(catColor, 0.1)}` },
              }}>
                <HubOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }} noWrap>{asset.name}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.4 }}>
                  <Tooltip title="Copy asset code" arrow>
                    <Stack direction="row" alignItems="center" spacing={0.4} onClick={() => { navigator.clipboard.writeText(asset.asset_code); toast.success('Copied!') }}
                      sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, transition: 'color 0.15s' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'inherit' }}>{asset.asset_code}</Typography>
                      <ContentCopyOutlinedIcon sx={{ fontSize: 10, opacity: 0.5 }} />
                    </Stack>
                  </Tooltip>
                  {asset.category_name && (
                    <>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>&middot;</Typography>
                      <Chip label={asset.category_name} size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                          bgcolor: alpha(catColor, 0.1), color: catColor, '& .MuiChip-label': { px: 0.75 } }} />
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 2 }}>
              <Tooltip title={can('assets:update') ? 'Edit asset' : "You don't have permission"} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Button variant="text" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setEditOpen(true)} disabled={!can('assets:update')}
                    sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', textTransform: 'none', minWidth: 0, px: 1.25, borderRadius: 1.5,
                      '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) } }}>
                    Edit
                  </Button>
                </Box>
              </Tooltip>
              <Tooltip title="Show QR code" arrow>
                <IconButton size="small" onClick={() => setQrOpen(true)}
                  sx={{ color: 'text.secondary', borderRadius: 1.5,
                    '&:hover': { color: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.06) } }}>
                  <QrCode2OutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={can('assets:delete') ? 'Delete' : "You don't have permission"} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <IconButton size="small" disabled={!can('assets:delete') || deleteM.isPending} onClick={async () => {
                    const ok = await confirmDlg({ title: `Delete ${asset.name}?`, message: 'This asset will be permanently removed.', confirmLabel: 'Delete', variant: 'danger' })
                    if (ok) deleteM.mutate()
                  }} sx={{ color: 'text.disabled', borderRadius: 1.5, '&:hover': { color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.06) } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Status chips + location */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={0.75} sx={{ mt: 1.75 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap rowGap={0.75}>
              <Tooltip
                title={`${STATUS_DESCRIPTIONS[asset.status] ?? ''} Set manually — work orders don't change this automatically.`}
                arrow
              >
                <Chip
                  icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important', color: `${st.color} !important` }} />}
                  label={st.label} size="small"
                  sx={{
                    height: 24, fontSize: 11.5, fontWeight: 600,
                    bgcolor: isDark ? alpha(st.color, 0.15) : st.bg, color: st.color,
                    border: '1px solid', borderColor: alpha(st.color, 0.2),
                    '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
                    cursor: 'default',
                  }}
                />
              </Tooltip>
              {(() => {
                const crit = CRITICALITY_META[asset.criticality] ?? { label: 'Normal', color: '#059669', bg: '#ECFDF5' }
                return (
                  <Chip label={crit.label} size="small"
                    sx={{
                      height: 24, fontSize: 11.5, fontWeight: 600,
                      bgcolor: isDark ? alpha(crit.color, 0.15) : crit.bg, color: crit.color,
                      border: '1px solid', borderColor: alpha(crit.color, 0.2),
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )
              })()}
              {warranty && (
                <Chip label={warranty.label} size="small"
                  sx={{
                    height: 24, fontSize: 11.5, fontWeight: 600,
                    bgcolor: isDark ? alpha(warranty.color, 0.15) : warranty.bg, color: warranty.color,
                    border: '1px solid', borderColor: alpha(warranty.color, 0.2),
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Stack>
            {locationStr && (
              <Stack direction="row" alignItems="center" spacing={0.5}
                onClick={() => {
                  if (asset.location_id) navigate(`/locations/${asset.location_id}`)
                  else if (asset.site_id) navigate(`/locations?site=${asset.site_id}`)
                }}
                sx={{ cursor: 'pointer', px: 1, py: 0.35, borderRadius: 1, transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) } }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>{locationStr}</Typography>
              </Stack>
            )}
          </Stack>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ px: 3.5, minHeight: 38, borderBottom: '1px solid', borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 38, textTransform: 'none', fontSize: 13.5, fontWeight: 600,
            color: 'text.secondary', px: 0, mr: 3.5,
            transition: 'color 0.15s',
            '&.Mui-selected': { color: 'primary.main' },
          },
          '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0', bgcolor: 'primary.main' },
        }}>
        <Tab value="insights" label="Insights" />
        {/* Shown when there's an Arcos link, existing soft points to
            display, or the viewer can create the first one — otherwise
            an asset with neither yet would have no way to reach the
            "add a soft point" UI at all. */}
        {(meta.iot?.arcos_folder_id || hasSoftPoints || can('assets:update')) && <Tab value="telemetry" label="Telemetry" />}
        {hasSoftPoints && <Tab value="trends" label="Trends" />}
        <Tab value="details" label="Details" />
        <Tab value="history" label="History" />
      </Tabs>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' } }}>
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}>
            {tab === 'insights' && <InsightsTab asset={asset} meta={meta} healthScore={healthScore} healthLabel={healthLabel} healthColor={healthColor} fmt={{ formatDate, formatCurrency, currencySymbol: prefs.currencySymbol }} stats={stats} />}
            {tab === 'telemetry' && (meta.iot?.arcos_folder_id || hasSoftPoints || can('assets:update')) && <AssetTelemetryPanel assetId={asset.id} meta={meta} />}
            {tab === 'trends' && hasSoftPoints && <AssetTrendsPanel assetId={asset.id} />}
            {tab === 'details' && <DetailsTab asset={asset} meta={meta} onNavigate={navigate} fmt={{ formatDate, formatCurrency, currencySymbol: prefs.currencySymbol }} />}
            {tab === 'history' && <HistoryTab asset={asset} meta={meta} fmt={{ formatDate, formatCurrency, currencySymbol: prefs.currencySymbol }} onNavigate={navigate} />}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Bottom bar */}
      <Box sx={{ flexShrink: 0, px: 3.5, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center', gap: 1 }}>
        {/* Task variant — lives in the Tasks module (/tasks), same
            convention as LocationDetail / MemberDetail's paired
            buttons. This asset detail page previously only offered
            "Use in new work order", so there was no way to create a
            task pre-filled with this asset from here. */}
        {can('tasks:create') && (
          <Button variant="outlined"
            onClick={() => navigate(`/tasks?new=1&asset_id=${asset.id}`)}
            sx={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8', borderColor: '#1D4ED8', borderRadius: 99, px: 2.5, py: 0.65, textTransform: 'none',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: alpha('#1D4ED8', 0.06), transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${alpha('#1D4ED8', 0.2)}` },
            }}>
            Use in new task
          </Button>
        )}
        {can('work_orders:create') && (
          <Button variant="outlined" startIcon={<BuildOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate(`/work-orders?new=1&asset_id=${asset.id}`)}
            sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', borderColor: 'primary.main', borderRadius: 99, px: 2.5, py: 0.65, textTransform: 'none',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06), transform: 'translateY(-1px)', boxShadow: (t) => `0 4px 12px ${alpha(t.palette.primary.main, 0.2)}` },
            }}>
            Use in new work order
          </Button>
        )}
      </Box>

      {editOpen && <AssetFormDialog mode="edit" asset={asset} categories={categories} sites={sites} onClose={() => setEditOpen(false)} />}

      <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)}
        payload={qrPayload.asset(asset.id)}
        title={asset.name}
        subtitle={asset.asset_code ?? undefined}
        kind="Asset" />
    </motion.div>
  )
}


/* ===================================================================
   INSIGHTS TAB
   =================================================================== */

interface AssetStats { spend: number[]; months: string[]; completedWos: number; totalWos: number }

function InsightsTab({ asset, meta, healthScore, healthLabel, healthColor, fmt, stats }: {
  asset: Asset; meta: Record<string, any>; healthScore: number; healthLabel: string; healthColor: string; fmt: Fmt; stats?: AssetStats
}) {
  const { formatDate, formatCurrency } = fmt
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const now = new Date()
  const primary = theme.palette.primary.main
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()

  // Active preventive WO for this asset (open/in_progress). Drives the
  // "Upcoming" item — clickable when scheduled, falls back to a
  // "Schedule" button when an interval is set but no WO is queued.
  const pmWoQ = useQuery({
    queryKey: ['asset-pm-wo', asset.id],
    queryFn: () => api.assets.getActivePmWorkOrder(asset.id),
    staleTime: 30_000,
  })
  const pmWo = pmWoQ.data ?? null

  const schedulePmM = useMutation({
    mutationFn: () => api.assets.schedulePm(asset.id),
    onSuccess: (wo) => {
      toast.success('Preventive maintenance scheduled')
      qc.invalidateQueries({ queryKey: ['asset-pm-wo', asset.id] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      if (wo?.id) navigate(`/work-orders/${wo.id}`)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to schedule'),
  })

  const intervalDays = parseInt(String(meta.maintenance_interval ?? '0'), 10)
  const hasInterval = intervalDays > 0

  // Pick the date to surface: if there's a queued WO, prefer its due_date
  // (the source of truth). Otherwise fall back to the asset's own
  // next_maintenance_date metadata so the previous behaviour is preserved.
  const upcomingDateRaw = pmWo?.due_date ?? meta.next_maintenance_date ?? null
  const maintenanceDue = upcomingDateRaw ? daysBetween(now.toISOString(), upcomingDateRaw) : null
  const warrantyDays = asset.warranty_expiry ? daysBetween(now.toISOString(), asset.warranty_expiry) : null

  const monthLabels = stats?.months ?? (() => {
    const labels: string[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date()
      d.setMonth(d.getMonth() - i)
      labels.push(d.toLocaleDateString(undefined, { month: 'short' }))
    }
    return labels
  })()

  const spendData: number[] = stats?.spend ?? Array(12).fill(0)
  const totalSpend = spendData.reduce((a, b) => a + b, 0)
  const maxSpend = Math.max(...spendData, 1)
  const [hoveredBar, setHoveredBar] = useState<number | null>(null)

  return (
    <Box sx={{ px: 3.5, py: 0.5 }}>
      {/* Health score — flat section, no card border */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={3.5}>
          <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <CircularProgress variant="determinate" value={100} size={110} thickness={3}
              sx={{ color: (t) => alpha(t.palette.divider, 0.3), position: 'absolute' }} />
            <CircularProgress variant="determinate" value={healthScore} size={110} thickness={3}
              sx={{ color: healthColor, '& .MuiCircularProgress-circle': { strokeLinecap: 'round', transition: 'stroke-dashoffset 1s ease' } }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: healthColor }}>{healthScore}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', mt: 0.25 }}>/ 100</Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.25 }}>Health</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: healthColor, lineHeight: 1.2 }}>{healthLabel}</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
              {healthScore >= 80 ? 'No action needed — running smoothly.' : healthScore >= 50 ? 'Some issues may need attention.' : 'Immediate attention required.'}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Activity stats — flat row with dividers */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 2.5 }}>
        <SectionLabel sx={{ mb: 1.5 }}>Activity</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
          <ActivityStat icon={<AssignmentOutlinedIcon />} label="Open work orders" value="0" />
          <ActivityStat icon={<CheckCircleOutlinedIcon />} label="Completed WOs" value={String(stats?.completedWos ?? 0)} border />
          <ActivityStat icon={<AccessTimeOutlinedIcon />} label="Downtime 365d" value="—" border />
          <ActivityStat icon={<Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{fmt.currencySymbol}</Typography>} label="Spend 12 mo" value={totalSpend > 0 ? formatCurrency(totalSpend) : '—'} border />
        </Box>
      </Box>

      {/* Maintenance spend chart — flat, no card */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
          <Box>
            <SectionLabel sx={{ mb: 0 }}>Maintenance spend</SectionLabel>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Last 12 months</Typography>
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{totalSpend > 0 ? formatCurrency(totalSpend) : '—'}</Typography>
        </Stack>
        <Box sx={{ mt: 2.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ height: 80 }}>
            {spendData.map((v, i) => (
              <Tooltip key={i} title={`${monthLabels[i]}: ${formatCurrency(v)}`} arrow placement="top">
                <Box
                  onMouseEnter={() => setHoveredBar(i)}
                  onMouseLeave={() => setHoveredBar(null)}
                  sx={{
                    flex: 1, borderRadius: '3px 3px 0 0', cursor: 'pointer',
                    height: `${Math.max(5, (v / maxSpend) * 100)}%`,
                    bgcolor: i === spendData.length - 1
                      ? primary
                      : hoveredBar === i
                        ? alpha(primary, 0.6)
                        : (t) => alpha(t.palette.text.disabled, 0.15),
                    transition: 'background-color 0.2s',
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
            {monthLabels.map((m, i) => (
              <Typography key={i} sx={{ flex: 1, fontSize: 9.5, textAlign: 'center', color: 'text.disabled' }}>
                {i === 0 ? m : i === 11 ? 'This month' : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Upcoming — flat list */}
      <Box sx={{ py: 2.5 }}>
        <SectionLabel>Upcoming</SectionLabel>
        {pmWo ? (
          <UpcomingItem
            title="Preventive maintenance"
            sub={[pmWo.wo_code, hasInterval ? `Every ${intervalDays} days` : null].filter(Boolean).join(' · ')}
            date={pmWo.due_date ? formatDate(pmWo.due_date) : 'No due date'}
            overdue={maintenanceDue !== null && maintenanceDue < 0}
            dueSoon={maintenanceDue !== null && maintenanceDue >= 0 && maintenanceDue < 7}
            onClick={() => navigate(`/work-orders/${pmWo.id}`)}
          />
        ) : hasInterval && can('work_orders:create') ? (
          <Box sx={{ py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Preventive maintenance</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.15 }}>
                  No work order scheduled · Every {intervalDays} days
                </Typography>
              </Box>
              <Button size="small" variant="outlined"
                startIcon={<BuildOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={() => schedulePmM.mutate()}
                disabled={schedulePmM.isPending}
                sx={{ fontSize: 12, fontWeight: 600, borderRadius: 99, textTransform: 'none', flexShrink: 0 }}>
                {schedulePmM.isPending ? 'Scheduling…' : 'Schedule now'}
              </Button>
            </Stack>
          </Box>
        ) : meta.next_maintenance_date ? (
          <UpcomingItem
            title="Preventive maintenance"
            sub={hasInterval ? `Every ${intervalDays} days` : undefined}
            date={formatDate(meta.next_maintenance_date)}
            overdue={maintenanceDue !== null && maintenanceDue < 0}
            dueSoon={maintenanceDue !== null && maintenanceDue >= 0 && maintenanceDue < 7}
          />
        ) : null}
        {asset.warranty_expiry && warrantyDays !== null && warrantyDays > 0 && warrantyDays < 90 && (
          <UpcomingItem
            title="Warranty expiring"
            sub={`${warrantyDays} days remaining`}
            date={formatDate(asset.warranty_expiry)}
            overdue={false}
            dueSoon={warrantyDays < 30}
          />
        )}
        {!pmWo && !hasInterval && !meta.next_maintenance_date && (warrantyDays === null || warrantyDays >= 90) && (
          <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>Nothing upcoming</Typography>
        )}
      </Box>
    </Box>
  )
}


/* ===================================================================
   DETAILS TAB
   =================================================================== */

function DetailsTab({ asset, meta, onNavigate, fmt }: { asset: Asset; meta: Record<string, any>; onNavigate: (path: string) => void; fmt: Fmt }) {
  const { formatDate, formatCurrency } = fmt
  const { can } = usePermissions()
  const locationParts = [asset.site_name, asset.location_name].filter(Boolean)
  const locationStr = locationParts.join(' \u00b7 ')

  return (
    <Box sx={{ px: 3.5, py: 0.5 }}>
      {/* Description */}
      {asset.description && (
        <FlatSection title="Description">
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.7 }}>{asset.description}</Typography>
        </FlatSection>
      )}

      {/* Identification */}
      <FlatSection title="Identification">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6 }}>
          <FlatKv label="Asset ID" value={asset.asset_code} mono tooltip="Unique asset identifier" />
          <FlatKv label="Category" value={asset.category_name ?? '—'}
            href={asset.category_id ? `/categories/${asset.category_id}` : undefined}
            tooltip="View category details" navigate={onNavigate} />
          {asset.serial_number && <FlatKv label="Serial number" value={asset.serial_number} mono tooltip="Manufacturer serial number" />}
        </Box>
      </FlatSection>

      {/* Manufacturer */}
      {(asset.manufacturer || asset.model) && (
        <FlatSection title="Manufacturer">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6 }}>
            <FlatKv label="Manufacturer" value={asset.manufacturer ?? '—'} />
            <FlatKv label="Model" value={asset.model ?? '—'} />
            {meta.manufactured_date && <FlatKv label="Manufactured" value={formatDate(meta.manufactured_date)} />}
          </Box>
        </FlatSection>
      )}

      {/* Location & criticality */}
      <FlatSection title="Location & criticality">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6 }}>
          <FlatKv label="Location" value={locationStr || '—'}
            href={asset.location_id ? `/locations/${asset.location_id}` : undefined}
            tooltip="View in locations tree" navigate={onNavigate} />
          <FlatKv label="Criticality" value={(CRITICALITY_META[asset.criticality] ?? { label: 'Normal' }).label}
            tooltip="How critical this asset is to operations" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6, mt: 2.5 }}>
          <FlatKv label="Status" value={STATUS_META[asset.status]?.label ?? asset.status} />
        </Box>
      </FlatSection>

      {/* Vendor */}
      {meta.vendor_name && (
        <FlatSection title="Vendor / supplier">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6 }}>
            <FlatKv label="Vendor" value={meta.vendor_name}
              href={meta.vendor_id ? `/vendors/${meta.vendor_id}` : undefined}
              tooltip="View vendor details" navigate={onNavigate} />
          </Box>
        </FlatSection>
      )}

      {/* Lifecycle */}
      <FlatSection title="Lifecycle">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.5, columnGap: 6 }}>
          {asset.purchase_date && <FlatKv label="Purchased" value={formatDate(asset.purchase_date)} tooltip="Date the asset was purchased" />}
          {asset.purchase_cost != null && <FlatKv label="Cost" value={formatCurrency(Number(asset.purchase_cost))} tooltip="Original purchase cost" />}
          {asset.warranty_expiry && <FlatKv label="Warranty expires" value={formatDate(asset.warranty_expiry)} tooltip="Warranty coverage end date" />}
          {meta.last_maintenance_date && <FlatKv label="Last serviced" value={formatDate(meta.last_maintenance_date)} tooltip="Most recent maintenance date" />}
          {meta.next_maintenance_date && <FlatKv label="Next preventive" value={formatDate(meta.next_maintenance_date)} tooltip="Next scheduled maintenance" />}
          {meta.maintenance_interval && <FlatKv label="Interval" value={`Every ${meta.maintenance_interval} days`} tooltip="Days between scheduled maintenance" />}
        </Box>
      </FlatSection>

      {/* Notes */}
      {/* Attachments */}
      <FlatSection title="Photos & documents">
        <AttachmentSection
          queryKey={['asset-attachments', asset.id]}
          listFn={() => api.assets.listAttachments(asset.id)}
          uploadFn={(file, onProgress) => api.assets.uploadAttachment(asset.id, file, onProgress)}
          viewFn={(doc) => api.assets.viewAttachment(asset.id, doc.id)}
          deleteFn={(docId) => api.assets.deleteAttachment(asset.id, docId)}
          canEdit={can('assets:update')}
          maxFiles={10}
        />
      </FlatSection>

      {/* Linked checklists */}
      <AssetChecklistsSection assetId={asset.id} />

      {asset.notes && (
        <FlatSection title="Notes" noBorder>
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{asset.notes}</Typography>
        </FlatSection>
      )}
    </Box>
  )
}

function AssetChecklistsSection({ assetId }: { assetId: string }) {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const [addOpen, setAddOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')

  const clQ = useQuery({ queryKey: ['asset-checklists', assetId], queryFn: () => api.assets.listChecklists(assetId) })
  const linked = Array.isArray(clQ.data) ? clQ.data : []

  const allQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const allChecklists = Array.isArray(allQ.data) ? allQ.data : []

  const addM = useMutation({
    mutationFn: (templateId: string) => api.assets.addChecklist(assetId, templateId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['asset-checklists', assetId] }); toast.success('Checklist linked'); setAddOpen(false); setSelectedTemplateId('') },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const removeM = useMutation({
    mutationFn: (templateId: string) => api.assets.removeChecklist(assetId, templateId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['asset-checklists', assetId] }); toast.success('Checklist unlinked') },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const navigate = useNavigate()

  return (
    <FlatSection title="Linked checklists">
      {linked.length > 0 ? (
        <Stack spacing={1}>
          {linked.map((cl) => (
            <Stack key={cl.id} direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 2, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => navigate(`/checklists/${cl.template_id}`)}>
              <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{cl.name}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>v{cl.version}</Typography>
              </Box>
              {can('assets:update') && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeM.mutate(cl.template_id) }}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No checklists linked to this asset</Typography>
      )}
      {can('assets:update') && (
        <>
          <Button size="small" startIcon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />} onClick={() => setAddOpen(true)}
            sx={{ mt: 1.5, fontSize: 12, fontWeight: 600, color: 'primary.main' }}>Link checklist</Button>
          <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Link checklist to asset</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <SearchableSelect
                  value={selectedTemplateId}
                  onChange={setSelectedTemplateId}
                  options={allChecklists.filter((c) => !linked.some((l) => l.template_id === c.id))}
                  getValue={(c) => c.id}
                  getLabel={(c) => c.name}
                  placeholder="Select a checklist"
                  renderOption={(c) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
                      <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                    </Stack>
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
              <Button variant="contained" disabled={!selectedTemplateId || addM.isPending} onClick={() => addM.mutate(selectedTemplateId)}>
                {addM.isPending ? 'Linking...' : 'Link'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </FlatSection>
  )
}


/* ===================================================================
   HISTORY TAB
   =================================================================== */

function HistoryTab({ asset, meta, fmt, onNavigate }: { asset: Asset; meta: Record<string, any>; fmt: Fmt; onNavigate: (path: string) => void }) {
  const [subTab, setSubTab] = useState<'meta' | 'work_orders'>('meta')

  return (
    <Box sx={{ px: 3.5, py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <SectionLabel sx={{ mb: 0 }}>{subTab === 'meta' ? 'Timeline' : 'Work orders'}</SectionLabel>
        <ToggleButtonGroup size="small" value={subTab} exclusive onChange={(_, v) => v && setSubTab(v)}>
          <ToggleButton value="meta" sx={{ height: 28, px: 1.5, fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
            Meta Data
          </ToggleButton>
          <ToggleButton value="work_orders" sx={{ height: 28, px: 1.5, fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
            Work Orders
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {subTab === 'meta'
        ? <MetaDataHistoryPanel asset={asset} meta={meta} fmt={fmt} />
        : <AssetWorkOrderHistory assetId={asset.id} onNavigate={onNavigate} />}
    </Box>
  )
}

function MetaDataHistoryPanel({ asset, meta, fmt }: { asset: Asset; meta: Record<string, any>; fmt: Fmt }) {
  const { formatDate, formatCurrency } = fmt
  const events = useMemo(() => {
    const items: { icon: React.ReactNode; title: string; sub?: string; user?: string; date: string; sortDate: string; color: string }[] = []

    items.push({
      icon: <AddCircleOutlineIcon />,
      title: 'Asset added to inventory',
      user: asset.created_by_name ?? undefined,
      date: formatDate(asset.created_at),
      sortDate: asset.created_at,
      color: '#3B82F6',
    })

    if (meta.last_maintenance_date) {
      items.push({
        icon: <HandymanOutlinedIcon />,
        title: 'Preventive PM completed',
        sub: 'All parameters normal.',
        user: asset.created_by_name ?? undefined,
        date: formatDate(meta.last_maintenance_date),
        sortDate: meta.last_maintenance_date,
        color: '#059669',
      })
    }

    if (meta.maintenance_interval && meta.last_maintenance_date) {
      const lastDate = new Date(meta.last_maintenance_date)
      const interval = parseInt(meta.maintenance_interval)
      if (interval > 0) {
        const prevDate = new Date(lastDate)
        prevDate.setDate(prevDate.getDate() - interval)
        items.push({
          icon: <HandymanOutlinedIcon />,
          title: 'Quarterly maintenance',
          sub: 'Routine inspection and servicing.',
          user: asset.created_by_name ?? undefined,
          date: formatDate(prevDate.toISOString()),
          sortDate: prevDate.toISOString(),
          color: '#059669',
        })
      }
    }

    if (asset.purchase_date) {
      items.push({
        icon: <InventoryOutlinedIcon />,
        title: 'Asset purchased',
        sub: asset.purchase_cost != null ? formatCurrency(Number(asset.purchase_cost)) : undefined,
        user: asset.created_by_name ?? undefined,
        date: formatDate(asset.purchase_date),
        sortDate: asset.purchase_date,
        color: '#8B5CF6',
      })
    }

    if (asset.updated_at !== asset.created_at) {
      items.push({
        icon: <EditOutlinedIcon />,
        title: 'Asset details updated',
        user: asset.created_by_name ?? undefined,
        date: formatDate(asset.updated_at),
        sortDate: asset.updated_at,
        color: '#D97706',
      })
    }

    return items.sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime())
  }, [asset, meta])

  return (
    <Box>
      {events.map((ev, i) => (
        <TimelineRow key={i} icon={ev.icon} title={ev.title} sub={ev.sub} user={ev.user} date={ev.date} color={ev.color} isLast={i === events.length - 1} />
      ))}
    </Box>
  )
}



/* ===================================================================
   Shared sub-components
   =================================================================== */

function SectionLabel({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Typography sx={{
      fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
      color: 'text.secondary', mb: 1.5, ...sx,
    }}>
      {children}
    </Typography>
  )
}

function FlatSection({ title, children, noBorder }: { title: string; children: React.ReactNode; noBorder?: boolean }) {
  return (
    <Box sx={{ borderBottom: noBorder ? 'none' : '1px solid', borderColor: 'divider', py: 2.5 }}>
      <Typography sx={{
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'text.secondary', mb: 1.75,
      }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function FlatKv({ label, value, mono, href, tooltip, navigate: nav }: {
  label: string; value: string; mono?: boolean; href?: string; tooltip?: string; navigate?: (path: string) => void
}) {
  const content = href && nav ? (
    <Tooltip title={tooltip ?? `View ${label.toLowerCase()}`} arrow placement="top-start">
      <Typography component="span"
        onClick={() => nav(href)}
        sx={{
          fontSize: 14.5, fontWeight: 500, color: 'primary.main', cursor: 'pointer',
          fontFamily: mono ? 'ui-monospace, "SFMono-Regular", monospace' : undefined,
          textDecoration: 'none', display: 'inline-block',
          '&:hover': { textDecoration: 'underline' },
        }}>
        {value}
      </Typography>
    </Tooltip>
  ) : tooltip ? (
    <Tooltip title={tooltip} arrow placement="top-start">
      <Typography component="span" sx={{
        fontSize: 14.5, fontWeight: 400, display: 'inline-block',
        fontFamily: mono ? 'ui-monospace, "SFMono-Regular", monospace' : undefined,
        cursor: 'help',
      }}>
        {value}
      </Typography>
    </Tooltip>
  ) : (
    <Typography sx={{
      fontSize: 14.5, fontWeight: 400,
      fontFamily: mono ? 'ui-monospace, "SFMono-Regular", monospace' : undefined,
    }}>
      {value}
    </Typography>
  )

  return (
    <Box>
      <Typography sx={{
        fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'text.secondary', mb: 0.5,
      }}>
        {label}
      </Typography>
      {content}
    </Box>
  )
}

function ActivityStat({ icon, label, value, border }: { icon: React.ReactNode; label: string; value: string; border?: boolean }) {
  return (
    <Box sx={{
      py: 1.5, px: 1.5,
      borderLeft: border ? '1px solid' : 'none', borderColor: 'divider',
    }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
        <Box sx={{ color: 'text.disabled', display: 'flex', '& svg': { fontSize: 13 } }}>{icon}</Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
    </Box>
  )
}

function UpcomingItem({ title, sub, date, overdue, dueSoon, onClick }: {
  title: string; sub?: string; date: string; overdue: boolean; dueSoon: boolean
  onClick?: () => void
}) {
  const dot = overdue ? '#DC2626' : dueSoon ? '#D97706' : undefined
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}
      onClick={onClick}
      sx={{
        py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        mx: onClick ? -1.5 : 0, px: onClick ? 1.5 : 0, borderRadius: onClick ? 1.5 : 0,
        transition: 'background-color 0.15s',
        '&:hover': onClick ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.05) } : undefined,
      }}>
      <Box sx={{ mt: 0.5, width: 6, height: 6, borderRadius: '50%', bgcolor: dot ?? 'text.disabled', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.15 }}>{sub}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: dot ?? 'text.secondary', fontWeight: dot ? 600 : 400, flexShrink: 0 }}>{date}</Typography>
    </Stack>
  )
}

function TimelineRow({ icon, title, sub, user, date, color, isLast }: {
  icon: React.ReactNode; title: string; sub?: string; user?: string; date: string; color: string; isLast: boolean
}) {
  const initials = user ? user.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : ''

  return (
    <Box sx={{ borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}
        sx={{
          py: 2.25, px: 1.5, mx: -1.5, borderRadius: 2,
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: (t) => alpha(t.palette.divider, 0.06) },
        }}>
        {/* Icon */}
        <Box sx={{ color: alpha(color, 0.7), mt: 0.25, flexShrink: 0, '& svg': { fontSize: 20 } }}>
          {icon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{title}</Typography>
          {sub && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.4 }}>{sub}</Typography>}
          {user && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', bgcolor: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{initials}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{user}</Typography>
            </Stack>
          )}
        </Box>

        {/* Date */}
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0, mt: 0.15, whiteSpace: 'nowrap' }}>{date}</Typography>
      </Stack>
    </Box>
  )
}
